import { useCallback, useEffect, useState, useRef } from "react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import classNames from "classnames";
import {
  BUTTON_TEXT,
  HTTP_METHODS,
  REGEX,
  EMPTY_STRING,
} from "../../../../constants";
import Drawer, { selectSelectedDrawer, setSelectedDrawer } from "../../Drawer";
import { actions as alertActions } from "../../Alert/alert.reducers";
import {
  selectSelectedDataModelId,
  createEndpoint,
  selectEndpoints,
  selectServices,
  selectSelectedEndpoint,
  setSelectedEndpoint,
  fetchEndpoints,
  updateEndpoint,
  fetchServices,
  selectNewService,
} from "../../../../pages/DataModels";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import isEqual from "lodash/isEqual";
import { setSelectedModal } from "../../Modal";
import { MODALS } from "../../AppModals/modals.constants";
import { VALIDATION_MESSAGES, ALERT_MESSAGES } from "../../../../constants";
import { actions } from "../../../../pages/DataModels/dataModels.reducers";

const { SUBMIT } = BUTTON_TEXT;
const { setAlert } = alertActions;
const { CREATE_ENDPOINT, UPDATE_ENDPOINT } = SPINNER_NAMES;
const { MANAGE_SERVICE_MODAL } = MODALS;
const {
  URL,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const defaultValues = {
  name: EMPTY_STRING,
  url: EMPTY_STRING,
  description: EMPTY_STRING,
  method: HTTP_METHODS.GET,
  serviceId: EMPTY_STRING,
};
const ENDPOINT = "endpoint";
const {
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  ENDPOINT_REQUIRED,
  ENDPOINT_URL_REQUIRED,
  URL_REQUIRED,
  SERVICE_IS_REQUIRED,
} = VALIDATION_MESSAGES;

const { ENDPOINT_EXISTED } = ALERT_MESSAGES;
const { setEndpointFilters } = actions;

const ManageEndpointDrawer = () => {
  const dispatch = useDispatch();
  const setFormikField = useRef({});

  const selectedDrawer = useSelector(selectSelectedDrawer);
  const selectedEndpoint = useSelector(selectSelectedEndpoint);
  const dataModelId = useSelector(selectSelectedDataModelId);
  const allEndpoints = useSelector(selectEndpoints);
  const allServices = useSelector(selectServices);
  const newService = useSelector(selectNewService);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [initialValues, setInitialValues] = useState(defaultValues);
  const [defaultService,setDefaultService] = useState();


  useEffect(() => {
    const { setServiceField } = setFormikField.current;
    if (defaultService && setServiceField) setServiceField(defaultService.id);
  }, [defaultService]);

  useEffect(() => {
    if (selectedEndpoint) {
      const { name, url, description, method, serviceId } = selectedEndpoint;
      setInitialValues({ name, url, description, method, serviceId });
    } 
  }, [selectedEndpoint]);


  useEffect(()=>{
    const recentlyCreatedService = allServices.find(
      ({ name }) =>
        name === newService?.name &&
        newService?.serviceType === "endpoint"
    );
    setDefaultService(recentlyCreatedService)
  },[allServices,newService])

  const handleClose = useCallback(() => {
    dispatch(setSelectedEndpoint(null));
    dispatch(setSelectedDrawer(null));
  }, [dispatch]);

  const handleOnSubmit = useCallback(
    (values) => {
      const { name, description, method, url, serviceId } = values;
      const isExistingEndpoint = allEndpoints.some(
        (endpoint) =>
          endpoint.id !== selectedEndpoint?.id &&
          endpoint.method === method &&
          endpoint.url === url
      );
      if (isExistingEndpoint) {
        dispatch(
          setAlert({
            message: ENDPOINT_EXISTED,
          })
        );
      } else {
        const endpointDetails = {
          name,
          description,
          url,
          dataModelId,
          method,
          serviceId,
        };
        dispatch(
          selectedEndpoint
            ? updateEndpoint({ id: selectedEndpoint.id, data: endpointDetails })
            : createEndpoint(endpointDetails)
        ).then((res) => {
          if (res.payload) {
            dispatch(fetchEndpoints({ id: dataModelId }));
            dispatch(fetchServices({ id: dataModelId }));
            dispatch(setEndpointFilters());
            handleClose();
          }
        });
      }
    },
    [dataModelId, dispatch, handleClose, allEndpoints, selectedEndpoint]
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(
        SPECIAL_CHARACTERS,
        NAME_SHOULD_NOT_CONTAIN
      )
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(ENDPOINT_REQUIRED),
    url: Yup.string()
      .matches(
        URL,
        ENDPOINT_URL_REQUIRED
      )
      .required(URL_REQUIRED),
    serviceId: Yup.string().required(SERVICE_IS_REQUIRED),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        errors,
        touched,
        isValid,
        handleReset,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={() => {
                handleReset();
                handleClose();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type={SUBMIT}
              disabled={
                isSpinnerActive || !isValid || isEqual(initialValues, values)
              }
            >
              <Spinner
                name={selectedEndpoint ? UPDATE_ENDPOINT : CREATE_ENDPOINT}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {SUBMIT}
              </Spinner>
            </button>
          </div>
        );
        return (
          <Form>
            <Drawer
              position="right"
              heading="Manage Endpoint"
              handleReset={handleReset}
              handleClose={handleClose}
              selectedDrawer={selectedDrawer}
              footer={footer}
            >
              <div className="scrollbar flex-1 overflow-auto">
                <div className="space-y-4 p-6 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
                      Endpoint Name <span className = "text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      type="text"
                      name="name"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Endpoint Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                    />
                    {touched.name && errors.name && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Url <span className = "text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      type="text"
                      name="url"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.url && errors.url,
                      })}
                      placeholder="Url"
                      value={values.url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.url && errors.url && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.url}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
                      Description
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      rows={1}
                      className={classNames(
                        "form-control min-h-28 scrollbar-light border-2",
                        {
                          "border-red-600":
                            touched.description && errors.description,
                        }
                      )}
                      placeholder="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.description && errors.description && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.description}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Method <span className = "text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      as="select"
                      name="method"
                      className="form-control bg-white flex items-center px-3 cursor-pointer"
                      value={values.method}
                      onChange={handleChange}
                    >
                      {Object.values(HTTP_METHODS).map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1">
                      Service <span className = "text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      as="select"
                      name="serviceId"
                      className="form-control mb-5 cursor-pointer"
                      value={values.serviceId}
                      onChange={handleChange}
                    >
                      <option value={EMPTY_STRING} disabled>
                        Select Endpoint Service
                      </option>
                      {allServices
                        .filter((service) => service.serviceType === ENDPOINT)
                        .map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name}
                          </option>
                        ))}
                    </Field>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary-light w-full"
                    onClick={() => {
                      dispatch(setSelectedModal(MANAGE_SERVICE_MODAL));
                      setFormikField.current = {
                        setServiceField: (value) =>
                          setFieldValue("serviceId", value),
                      };
                    }}
                  >
                    Add New Endpoint Service
                  </button>
                </div>
              </div>
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageEndpointDrawer;
